import {
  Facebook, Instagram, Loader2, Twitter, ExternalLink,
  Plus, Upload, Trash2, GripVertical, Save, Info, Image as ImageIcon
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import useEntity from "../hooks/useEntity";
import useStoreConfig from "../hooks/useStoreConfig";
import { FileService } from "../services/fileService";
import { storeService } from "../services/storeService";
import useToastStore from "../store/useToastStore";
import { Banner } from "../types/banner";
import { compressImageFile } from "../utils/imageCompression";
import { trackEvent } from "../utils/analyticsHelper";

interface FormData {
  brandName: string;
  emailEnabled: boolean;
  logo: File | null;
  logoUrl: string | null;
  banners: Banner[];
  socialLinks: {
    facebook: string;
    instagram: string;
    twitter: string;
  };
}

const StoreConfig = () => {
  const toast = useToastStore();
  const { data: entity } = useEntity();
  const { data, isLoading: isFetching, refetch } = useStoreConfig();
  const storeData = data as any;

  const [formData, setFormData] = useState<FormData>({
    brandName: "",
    emailEnabled: false,
    logo: null,
    logoUrl: null,
    banners: [],
    socialLinks: {
      facebook: "",
      instagram: "",
      twitter: ""
    }
  });

  const [isInitialized, setIsInitialized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompressingLogo, setIsCompressingLogo] = useState(false);
  const [compressingBanners, setCompressingBanners] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (storeData && !isInitialized) {
      setFormData({
        brandName: storeData.brandName || "",
        emailEnabled: storeData.emailEnabled ?? false,
        logo: null,
        logoUrl: storeData.logoUrl || null,
        banners: storeData.banners || [],
        socialLinks: {
          facebook: storeData.socialLinks?.facebook || "",
          instagram: storeData.socialLinks?.instagram || "",
          twitter: storeData.socialLinks?.twitter || ""
        }
      });
      setIsInitialized(true);
    }
  }, [storeData, isInitialized]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let logoUrl = formData.logoUrl;
      if (formData.logo) {
        const logoResponse = await FileService.uploadFiles([formData.logo]);
        logoUrl = logoResponse.files[0].url;
      }

      await storeService.updateStore({
        brandName: formData.brandName,
        emailEnabled: formData.emailEnabled,
        logoUrl,
        banners: formData.banners,
        socialLinks: formData.socialLinks
      });

      trackEvent('store_customized', {
        has_logo: !!logoUrl,
        has_banners: formData.banners.length > 0,
        has_social_links: Object.values(formData.socialLinks).some(v => !!v),
      });
      toast.showToast("Store configuration saved successfully!", "success");
      await refetch();
      // Reset the local file object so we don't re-upload if saved again
      setFormData(prev => ({ ...prev, logo: null }));
    } catch (error: any) {
      console.error("Error saving store config:", error);
      toast.showToast(error?.response?.data?.message || "Failed to save configuration", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSocialLinkChange = (platform: keyof FormData['socialLinks']) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [platform]: e.target.value
      }
    }));
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.showToast('Please upload a valid image file (JPG, PNG, GIF, WEBP)', 'error');
      return;
    }

    setIsCompressingLogo(true);
    try {
      const compressed = await compressImageFile(file);
      setFormData(prev => ({
        ...prev,
        logo: compressed,
        logoUrl: URL.createObjectURL(compressed)
      }));
    } catch (error) {
      toast.showToast("Failed to process logo image", "error");
    } finally {
      setIsCompressingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({
      ...prev,
      logo: null,
      logoUrl: null
    }));
  };

  const handleAddBanner = () => {
    const newBanner: Banner = {
      id: `temp-${Date.now()}`,
      imageUrl: "",
      link: "",
      isActive: true
    };
    setFormData(prev => ({
      ...prev,
      banners: [newBanner, ...prev.banners] // Add to top for better visibility
    }));
  };

  const handleBannerImageUpload = async (bannerId: string, file: File) => {
    try {
      const response = await FileService.uploadFiles([file]);
      const imageUrl = response.files[0].url;

      setFormData(prev => ({
        ...prev,
        banners: prev.banners.map(banner =>
          banner.id === bannerId ? { ...banner, imageUrl } : banner
        )
      }));
    } catch (error) {
      console.error("Error uploading banner image:", error);
      toast.showToast("Failed to upload banner image", "error");
    }
  };

  const handleBannerChange = (bannerId: string, field: keyof Banner, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      banners: prev.banners.map(banner =>
        banner.id === bannerId ? { ...banner, [field]: value } : banner
      )
    }));
  };

  const handleRemoveBanner = (bannerId: string) => {
    setFormData(prev => ({
      ...prev,
      banners: prev.banners.filter(banner => banner.id !== bannerId)
    }));
  };

  const handleBannerFileUpload = (bannerId: string) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.showToast('Please upload a valid image file', 'error');
      return;
    }
    setCompressingBanners(prev => ({ ...prev, [bannerId]: true }));
    try {
      const compressed = await compressImageFile(file);
      await handleBannerImageUpload(bannerId, compressed);
    } finally {
      setCompressingBanners(prev => ({ ...prev, [bannerId]: false }));
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(formData.banners);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setFormData(prev => ({ ...prev, banners: items }));
  };

  if (isFetching && !isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50/50 px-4">
        <div className="flex flex-col items-center gap-4 bg-white p-8 rounded-2xl shadow-sm border border-gray-100 max-w-sm w-full">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          <p className="text-gray-600 font-medium text-center">Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Store Configuration</h1>
              <p className="text-sm text-gray-500 mt-1">Manage your store's appearance and preferences</p>
            </div>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all shadow-sm hover:shadow-md disabled:opacity-70 disabled:cursor-not-allowed font-semibold w-full sm:w-auto"
            >
              {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              <span>{isSaving ? "Saving Changes..." : "Save Changes"}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">

          {/* Left Column */}
          <div className="xl:col-span-2 space-y-8">

            {/* General Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md">
              <div className="p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                    <Info className="w-5 h-5" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">General Settings</h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  {/* Brand Name */}
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-700">Brand Name</label>
                    <div className="relative group">
                      <input
                        type="text"
                        value={formData.brandName}
                        onChange={(e) => setFormData(prev => ({ ...prev, brandName: e.target.value }))}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 font-medium focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  {/* Order Emails Toggle */}
                  {entity?.entityId === 2739 && (
                    <div className="space-y-3">
                      <label className="block text-sm font-semibold text-gray-700">Order Emails</label>
                      <div
                        onClick={() => setFormData(prev => ({ ...prev, emailEnabled: !prev.emailEnabled }))}
                        className="flex items-center justify-between p-3 rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">Email Notifications</span>
                          <span className="text-xs text-gray-500">Send order updates to customers</span>
                        </div>
                        <div className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-300 ease-in-out ${formData.emailEnabled ? 'bg-green-500' : 'bg-gray-200'
                          }`}>
                          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-300 ease-in-out ${formData.emailEnabled ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Store Assets */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md">
              <div className="p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
                    <ImageIcon className="w-5 h-5" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Store Assets</h2>
                </div>

                {/* Logo Section */}
                <div className="mb-10">
                  <label className="block text-sm font-semibold text-gray-700 mb-4">Store Logo</label>
                  <div className="flex items-start gap-6">
                    <div className="relative group shrink-0">
                      <div className="w-32 h-32 sm:w-40 sm:h-40 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden transition-all group-hover:border-blue-400 group-hover:bg-blue-50/50">
                        {formData.logoUrl ? (
                          <img src={formData.logoUrl} alt="Store Logo" className="w-full h-full object-contain p-2" />
                        ) : (
                          <div className="flex flex-col items-center justify-center p-4 text-center">
                            <Upload className="w-8 h-8 text-gray-400 mb-2 group-hover:text-blue-500 transition-colors" />
                            <span className="text-xs text-gray-500 font-medium">Upload Logo</span>
                          </div>
                        )}
                        {isCompressingLogo && (
                          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center">
                            <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          onChange={handleLogoUpload}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          title="Upload logo"
                        />
                      </div>
                    </div>
                    <div className="flex-1 pt-2">
                      <h4 className="text-sm font-medium text-gray-900">Brand Identity</h4>
                      <p className="text-sm text-gray-500 mt-1 mb-4">Recommended size: 400x400 pixels.<br />PNG, JPG, or WEBP up to 2MB.</p>
                      {formData.logoUrl && (
                        <button
                          onClick={handleRemoveLogo}
                          className="text-sm font-medium text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-4 py-2 rounded-lg transition-colors inline-flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" /> Remove Logo
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="h-px bg-gray-100 my-8 w-full" />

                {/* Banners Section */}
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700">Hero Banners</label>
                      <p className="text-xs text-gray-500 mt-1">Drag to reorder. Recommended size: 1920x600px.</p>
                    </div>
                    <button
                      onClick={handleAddBanner}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors shadow-sm"
                    >
                      <Plus className="w-4 h-4" /> Add Banner
                    </button>
                  </div>

                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="store-banners">
                      {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
                          {formData.banners.length === 0 ? (
                            <div className="text-center py-12 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl">
                              <ImageIcon className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                              <p className="text-sm font-medium text-gray-900">No banners added</p>
                              <p className="text-xs text-gray-500 mt-1 mb-4">Upload banners to showcase your products or offers</p>
                              <button onClick={handleAddBanner} className="text-sm font-medium text-blue-600 hover:text-blue-700">
                                + Create your first banner
                              </button>
                            </div>
                          ) : (
                            formData.banners.map((banner, index) => (
                              <Draggable key={banner.id} draggableId={banner.id} index={index}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className={`bg-white border rounded-2xl overflow-hidden transition-all duration-200 ${snapshot.isDragging ? 'shadow-xl scale-[1.02] border-blue-300 ring-1 ring-blue-300' : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                                      }`}
                                  >
                                    <div className="flex flex-col sm:flex-row">
                                      {/* Drag Handle & Image Area */}
                                      <div className="relative w-full sm:w-64 h-40 sm:h-auto bg-gray-50 shrink-0 border-b sm:border-b-0 sm:border-r border-gray-200 group">
                                        {/* Drag Handle */}
                                        <div
                                          {...provided.dragHandleProps}
                                          className="absolute top-2 left-2 z-10 p-1.5 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing transition-colors"
                                        >
                                          <GripVertical className="w-4 h-4" />
                                        </div>

                                        {banner.imageUrl ? (
                                          <div className="w-full h-full relative">
                                            <img src={banner.imageUrl} alt="Banner" className="w-full h-full object-cover" />
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                              <label className="cursor-pointer bg-white/90 text-gray-900 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-white shadow-sm transition-colors">
                                                Change Image
                                                <input
                                                  type="file"
                                                  accept="image/*"
                                                  className="sr-only"
                                                  onChange={handleBannerFileUpload(banner.id)}
                                                />
                                              </label>
                                            </div>
                                          </div>
                                        ) : (
                                          <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors">
                                            <Upload className="w-6 h-6 text-gray-400 mb-2" />
                                            <span className="text-xs font-medium text-gray-500">Upload Image</span>
                                            <input
                                              type="file"
                                              accept="image/*"
                                              className="sr-only"
                                              onChange={handleBannerFileUpload(banner.id)}
                                            />
                                          </label>
                                        )}
                                        {compressingBanners[banner.id] && (
                                          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center">
                                            <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                                          </div>
                                        )}
                                      </div>

                                      {/* Banner Details Area */}
                                      <div className="p-5 flex-1 flex flex-col justify-between">
                                        <div className="space-y-4">
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs font-bold uppercase tracking-wider text-gray-500 bg-gray-100 px-2 py-1 rounded">Banner {index + 1}</span>

                                            <div className="flex items-center gap-4">
                                              {/* Status Toggle */}
                                              <label className="flex items-center cursor-pointer group/toggle">
                                                <div className="relative">
                                                  <input
                                                    type="checkbox"
                                                    className="sr-only"
                                                    checked={banner.isActive}
                                                    onChange={(e) => handleBannerChange(banner.id, 'isActive', e.target.checked)}
                                                  />
                                                  <div className={`block w-10 h-6 rounded-full transition-colors ${banner.isActive ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                                  <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${banner.isActive ? 'transform translate-x-4' : ''}`}></div>
                                                </div>
                                                <span className={`ml-2 text-xs font-medium transition-colors ${banner.isActive ? 'text-green-600' : 'text-gray-500 group-hover/toggle:text-gray-700'}`}>
                                                  {banner.isActive ? 'Active' : 'Hidden'}
                                                </span>
                                              </label>

                                              <button
                                                onClick={() => handleRemoveBanner(banner.id)}
                                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Delete banner"
                                              >
                                                <Trash2 className="w-4 h-4" />
                                              </button>
                                            </div>
                                          </div>

                                          <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Link URL (Optional)</label>
                                            <div className="relative">
                                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <ExternalLink className="w-4 h-4 text-gray-400" />
                                              </div>
                                              <input
                                                type="url"
                                                value={banner.link || ''}
                                                onChange={(e) => handleBannerChange(banner.id, 'link', e.target.value)}
                                                placeholder="https://your-store.com/collection"
                                                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all"
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ))
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="xl:col-span-1 space-y-8">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden sticky top-[100px] transition-all hover:shadow-md">
              <div className="p-6 sm:p-8 border-b border-gray-100 bg-gradient-to-br from-gray-50 to-white">
                <h2 className="text-xl font-bold text-gray-900 mb-2">Social Media</h2>
                <p className="text-sm text-gray-500">Connect your audience across platforms</p>
              </div>
              <div className="p-6 sm:p-8 space-y-6">
                {[
                  { id: 'facebook', icon: Facebook, color: 'text-[#1877F2]', bg: 'bg-[#1877F2]/10', placeholder: 'facebook.com/yourbrand' },
                  { id: 'instagram', icon: Instagram, color: 'text-[#E4405F]', bg: 'bg-[#E4405F]/10', placeholder: 'instagram.com/yourbrand' },
                  { id: 'twitter', icon: Twitter, color: 'text-[#1DA1F2]', bg: 'bg-[#1DA1F2]/10', placeholder: 'twitter.com/yourbrand' }
                ].map(({ id, icon: Icon, color, bg, placeholder }) => (
                  <div key={id} className="space-y-2 group">
                    <label className="block text-sm font-semibold text-gray-700 capitalize flex items-center justify-between">
                      {id}
                    </label>
                    <div className="relative flex items-center">
                      <div className={`absolute left-0 inset-y-0 w-12 flex items-center justify-center rounded-l-xl ${bg} border border-transparent group-focus-within:border-gray-200 transition-colors z-10`}>
                        <Icon className={`w-5 h-5 ${color}`} />
                      </div>
                      <input
                        type="url"
                        value={formData.socialLinks[id as keyof typeof formData.socialLinks]}
                        onChange={handleSocialLinkChange(id as keyof typeof formData.socialLinks)}
                        placeholder={placeholder}
                        className="w-full pl-14 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder:text-gray-400"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreConfig;